import { useEffect, useRef } from "react";
import { useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CloudOff, AlertTriangle } from "lucide-react";
import { Button } from "@nexus/ui";
import { ApiError } from "@/lib/api-client";
import { clearAuthLocalState } from "@/lib/query-client";
import { useServerStatusStore } from "@/hooks/serverStatusStore";
import { Sentry } from "@/lib/sentry";

interface AppUnavailableProps {
  error: Error;
  reset: () => void;
}

function isNetworkError(error: Error): boolean {
  if (!(error instanceof ApiError)) return false;
  return error.status === 0 || error.code === "OFFLINE" || error.code === "NETWORK_ERROR";
}

export function AppUnavailable({ error, reset }: AppUnavailableProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const serverStatus = useServerStatusStore((s) => s.status);
  const network = isNetworkError(error);
  const prevStatus = useRef<"online" | "offline" | null>(null);

  // Auto-recover on offline → online transition only. Store defaults to
  // "online" before the first health poll lands, so we must wait for a real
  // flip to avoid thrashing the loader.
  useEffect(() => {
    const prev = prevStatus.current;
    prevStatus.current = serverStatus;
    if (network && prev === "offline" && serverStatus === "online") {
      void router.invalidate().then(reset).catch((err: unknown) => {
        Sentry.captureException(err, { tags: { flow: "auto-recover" } });
      });
    }
  }, [network, serverStatus, router, reset]);

  const onRetry = () => {
    void router.invalidate().then(reset).catch((err: unknown) => {
      Sentry.captureException(err, { tags: { flow: "manual-retry" } });
    });
  };

  const onClearSession = () => {
    clearAuthLocalState();
    window.location.href = "/login";
  };

  const Icon = network ? CloudOff : AlertTriangle;
  const title = network ? t("errors.cantReachServer") : t("errors.pageLoadFailed");
  const description = network
    ? t("errors.cantReachServerDescription")
    : t("errors.pageLoadFailedDescription");

  return (
    <div className="flex h-dvh flex-col items-center justify-center gap-4 text-center p-8">
      <div className="rounded-full bg-[var(--muted)] p-6">
        <Icon className="h-10 w-10 text-[var(--muted-foreground)]" />
      </div>
      <h1 className="text-xl font-normal">{title}</h1>
      <p className="max-w-md text-sm text-[var(--muted-foreground)]">{description}</p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button variant="outline" onClick={onRetry}>
          {t("errors.retryNow")}
        </Button>
        <Button variant="ghost" onClick={onClearSession}>
          {t("errors.clearSessionAndReload")}
        </Button>
      </div>
    </div>
  );
}
